import type { PlatformContract } from "@ondit/publishing";
import { Button } from "@ondit/ui";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
	TOKEN_SERVICE_ACCOUNT_DESCRIPTORS,
	type TokenServiceAccountProviderId,
} from "@/lib/credentials/token-service-accounts/descriptors";
import { TokenServiceAccountModal } from "@/screens/workspace/integrations/components/connect-service-account-modal/token-service-account-modal";
import { ApiError, api } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys } from "~/core/query";

/**
 * Connecting the account a platform publishes through.
 *
 * Not a link to the Integrations page: that page is a list of the Agent's
 * blocks, and a platform this server publishes to needs no block — Bluesky,
 * Mastodon and Lemmy have none, and WordPress's and YouTube's blocks grant a
 * different provider than the publisher reads. The account lands in the same
 * credential table either way.
 */
export function ConnectAccount({
	entityId,
	platform,
	disabled,
	size,
	variant = "ghost",
	onCreated,
}: {
	entityId: string;
	platform: Pick<PlatformContract, "name" | "credential_kind" | "credential_provider">;
	disabled?: boolean;
	size?: "sm";
	variant?: "ghost" | "outline";
	onCreated?: (id: string) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const queryClient = useQueryClient();
	const [busy, setBusy] = useState(false);
	const [open, setOpen] = useState(false);
	const provider = platform.credential_provider;
	if (!provider) return null;
	const descriptor = TOKEN_SERVICE_ACCOUNT_DESCRIPTORS[provider as TokenServiceAccountProviderId];

	async function connect() {
		if (platform.credential_kind !== "oauth") return setOpen(true);
		setBusy(true);
		try {
			const { url } = await api.connectCredential(
				entityId,
				provider!,
				`${location.origin}${location.pathname}${location.search}`,
			);
			location.assign(url);
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
			setBusy(false);
		}
	}

	return (
		<>
			<Button
				variant={variant}
				size={size}
				className="self-start"
				disabled={disabled || busy}
				onClick={() => void connect()}
			>
				{t("publishing.platform.connectAccount")}
			</Button>
			{open && descriptor && (
				<TokenServiceAccountModal
					open
					onOpenChange={setOpen}
					workspaceId={entityId}
					descriptor={descriptor}
					serviceName={platform.name}
					serviceIcon={KeyRound}
					onCreated={(id) => {
						onCreated?.(id);
						void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
					}}
				/>
			)}
		</>
	);
}
